// components/ui/local-sidebar-rail.jsx
import React from 'react';
import { cn } from '../../utils/cn';

/**
 * LocalSidebarRail - A hover-activated edge control for collapsing/expanding local sidebars
 *
 * Inspired by shadcn's SidebarRail but designed for non-global, locally scoped sidebars
 * (e.g., Services section, Team section)
 *
 * Works with the shadcn-inspired slide-out architecture:
 * - Outer spacer collapses width
 * - Inner container slides off-screen with transform
 * - Rail stays accessible at the edge
 *
 * @param {boolean} collapsed - Whether the sidebar is currently collapsed
 * @param {function} onToggle - Callback to toggle the sidebar state
 * @param {string} side - Which side the rail is on ('left' | 'right')
 */
const LocalSidebarRail = React.forwardRef(
  ({ collapsed = false, onToggle, side = 'left', className, ...props }, ref) => {
    return (
      <button
        ref={ref}
        aria-label="Toggle Sidebar"
        tabIndex={-1}
        onClick={onToggle}
        title="Toggle Sidebar"
        className={cn(
          // Base - absolute positioned, full height, 16px hit area
          "absolute inset-y-0 z-20 hidden sm:flex w-4 transition-all duration-200 ease-linear",

          // Pseudo-element creates the 2px vertical line
          "after:absolute after:inset-y-0 after:w-[2px]",
          "after:transition-colors after:duration-200",

          // Line appears on hover - using slate for administrative layer
          "hover:after:bg-slate-600",

          // Collapsed state: thick bar on hover for better visibility
          collapsed && "hover:bg-slate-900/70",

          // LEFT SIDEBAR (rail on right edge)
          side === 'left' && !collapsed && [
            // Expanded: right edge of sidebar, centered on border
            "-right-4",
            "-translate-x-1/2", // shifts button left by 8px to center on border
            "after:left-1/2",
            "cursor-w-resize", // indicates "collapse left"
          ],
          side === 'left' && collapsed && [
            // Collapsed: right edge of the off-screen sidebar (which is left edge of viewport)
            "right-0",
            "translate-x-full", // positions it at viewport edge (compensates for sidebar translation)
            "after:left-0", // line at left edge of button
            "cursor-e-resize", // indicates "expand right"
          ],

          // RIGHT SIDEBAR (rail on left edge)
          side === 'right' && !collapsed && [
            "-left-4",
            "translate-x-1/2", // shifts button right by 8px to center on border
            "after:left-1/2",
            "cursor-e-resize",
          ],
          side === 'right' && collapsed && [
            "left-0",
            "-translate-x-full",
            "after:right-0",
            "cursor-w-resize",
          ],

          className
        )}
        {...props}
      />
    );
  }
);

LocalSidebarRail.displayName = 'LocalSidebarRail';

export { LocalSidebarRail };
